import { IApplicationAsset } from '@kurtosys/ksys-api-client/dist/models/requests/applicationManager/IApplicationAsset';
import { BaseStore } from './BaseStore';
import { computed, observable, action, isArrayLike } from 'mobx';
import { Services } from '../services';
import { DataStore } from './DataStore';
import { Manifest } from '../configuration/Manifest';
import { Stores } from '../models/Stores';
import { appendCacheQueryString } from '@kurtosys/ksys-app-template/dist/appsManager/utils';
import { CacheOptions } from '@kurtosys/ksys-api-client/dist/models/commonTypes';

export interface AssetsByFileName {
    [fileName: string]: IApplicationAsset;
}

export interface FontAsset {
    name: string;
    url: string;
}

export class AssetStore extends BaseStore {
    @observable.ref
    assets: Array<IApplicationAsset>;

    @observable.ref
    assetsByFileName: AssetsByFileName;

    @observable.ref
    assetsByOverrideFileName: AssetsByFileName;

    fontFamilies = [
        'HeaderFont',
        'BodyFont',
        'FontLight',
        'FontLightItalic',
        'FontThin',
        'FontThinItalic',
        'FontMedium',
        'FontMediumItalic',
        'FontRegular',
        'FontItalic',
        'FontSemiBold',
        'FontSemiBoldItalic',
        'FontBold',
        'FontBoldItalic',
        'FontBlack',
        'FontBlackItalic',
    ];

    constructor(stores: Stores, public dataStore: DataStore) {
        super(stores);
    }
    async init() {
        await this.getAssets();
    }

    get services(): Services {
        return this.dataStore && this.dataStore.services;
    }

    get applicationCode(): string {
        return Manifest.getApplicationGuid();
    }

    get apiUrl(): string {
        return this.dataStore.apiUrl;
    }

    @computed
    get cacheOptions(): CacheOptions {
        const cacheOptions =
            // asset &&
            this.dataStore &&
            this.dataStore.config &&
            this.dataStore.config.globalConfig &&
            this.dataStore.config.globalConfig.cacheOptions;
        return cacheOptions;
    }

    @computed
    get fonts(): Array<FontAsset> {
        const fonts = [];
        if (this.assets) {
            this.fontFamilies.map(font => {
                const asset = this.getFontFamilyAsset(font);
                if (asset) {
                    fonts.push({
                        name: font,
                        url: this.getUrlFromAsset(asset),
                    });
                }
            });
        }
        return fonts;
    }

    @computed
    get fontFaces(): string {
        /*
         * Generate @font-face for fonts.
         * To make use of a particular font, upload a font file named e.g. "FontLight.ttf" to application assets,
         * then reference it in styles by font-family name e.g. "FontLight".
         * Fonts not referenced in styles will not be downloaded.
         */
        const { props } = this as any;
        if (props) {
            return `
                ${this.fonts.map(font => {
                    return `@font-face {
                                font-family: '${font.name}';
                                src: url('${font.url}');
                                font-display: fallback;
                            }`;
                })}
        `;
        }
    }

    mapAssetsTo(
        key: keyof IApplicationAsset
    ): Record<string, IApplicationAsset> | undefined {
        if (this.assets) {
            return this.assets.reduce((acc: AssetsByFileName, asset) => {
                if (asset[key]) {
                    acc[asset[key]] = asset;
                }
                return acc;
            }, {});
        }
        return;
    }

    @action
    async getAssets() {
        try {
            if (this.services) {
                const assetsResponse = await this.services.getListAssets({
                    applicationCode: this.applicationCode,
                });
                this.assets =
                    assetsResponse && assetsResponse.applicationAssets;
                if (this.assets && isArrayLike(this.assets)) {
                    this.assetsByFileName = this.mapAssetsTo('fileName');
                    this.assetsByOverrideFileName = this.mapAssetsTo(
                        'assetToOverrideFileName'
                    );
                    this.loadFontFaces();
                }
            } else {
                console.warn('Services is undefined on AssetStore');
            }
        } catch (e) {
            console.warn(e);
        }
    }

    getUrlFromAsset(asset: IApplicationAsset) {
        return `${this.apiUrl}/${appendCacheQueryString(
            asset.fileName,
            asset as any,
            this.cacheOptions
        )}`;
    }

    getAssetUrl(fileName: string): string | undefined {
        const asset = this.getAssetByFileName(fileName);
        return `${this.apiUrl}/${appendCacheQueryString(
            fileName,
            asset as any,
            this.cacheOptions
        )}`;
    }

    getFontFamilyAsset(fontFamilyName: string) {
        const fileName = `${fontFamilyName}.ttf`;
        const overridingAsset = this.getAssetByOverrideFileName(fileName);
        if (overridingAsset) {
            return overridingAsset;
        }
        const asset = this.getAssetByFileName(fileName);
        if (asset && asset.assetToOverrideFileName === null) {
            return asset;
        }
    }

    getAssetByFileName(fileName: string): IApplicationAsset | undefined {
        return this.assetsByFileName && this.assetsByFileName[fileName];
    }

    getAssetByOverrideFileName(
        fileName: string
    ): IApplicationAsset | undefined {
        const asset =
            this.assetsByOverrideFileName &&
            this.assetsByOverrideFileName[fileName];
        return asset;
    }

    /**
     * This method is responsible for fetching font assets and loading them onto the html document (outside of the shadow dom)
     */
    loadFontFaces() {
        this.fonts.map(font => {
            const fontFace = new FontFace(font.name, `url('${font.url}')`);
            fontFace
                .load()
                .then(response => {
                    (document as any).fonts.add(response);
                })
                .catch(error => {
                    console.warn('Unable to load font-face from assets:', font);
                });
        });
    }
}



// WEBPACK FOOTER //
// ./src/stores/AssetStore.ts